import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Promise-based confirmation dialog. Replaces the dozen `window.confirm()`
 * calls scattered around admin pages — those are ugly, OS-native, can't
 * be styled, and don't render emoji nicely.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: '删除这条反馈？',
 *     message: '此操作不可恢复',
 *     tone: 'danger',
 *     confirmLabel: '确认删除',
 *   });
 *   if (ok) await deleteFeedback(id);
 *
 * Wrap once at the app root with <ConfirmProvider>. The dialog is
 * rendered inline (not portaled) — admin has no overflow:hidden ancestors
 * that would clip it, so the simpler implementation works.
 */

type Tone = 'info' | 'danger' | 'warning';

interface ConfirmOpts {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
}

interface PendingDialog extends ConfirmOpts {
  resolve: (value: boolean) => void;
}

type ConfirmFn = (opts: ConfirmOpts) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }): JSX.Element {
  const [pending, setPending] = useState<PendingDialog | null>(null);

  const confirm: ConfirmFn = useCallback((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  function handle(answer: boolean): void {
    if (pending !== null) {
      pending.resolve(answer);
      setPending(null);
    }
  }

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending !== null && (
        <DialogShell pending={pending} onAnswer={handle} />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (ctx === null) {
    throw new Error('useConfirm() called outside <ConfirmProvider>');
  }
  return ctx;
}

const TONE_BG: Record<Tone, string> = {
  info: 'border-white/10',
  warning: 'border-amber-700/60',
  danger: 'border-rose-700/60',
};

const TONE_BUTTON: Record<Tone, string> = {
  info: 'bg-shark-500 hover:bg-shark-400 text-white',
  warning: 'bg-amber-600 hover:bg-amber-500 text-white',
  danger: 'bg-rose-600 hover:bg-rose-500 text-white',
};

function DialogShell({
  pending,
  onAnswer,
}: {
  pending: PendingDialog;
  onAnswer: (b: boolean) => void;
}): JSX.Element {
  const tone = pending.tone ?? 'info';
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const onAnswerRef = useRef(onAnswer);
  onAnswerRef.current = onAnswer;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    // 危险/警告操作默认聚焦「取消」(更安全, APG Alert Dialog 标准); 普通操作聚焦「确认」。
    (tone === 'info' ? confirmRef : cancelRef).current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onAnswerRef.current(false);
    };
    document.addEventListener('keydown', onKey);
    return (): void => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时聚焦/绑定; onAnswer 走 ref 取最新
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 背景点击关闭 = 鼠标便利; aria-hidden 让它不进 a11y 树; 无障碍关闭走 Escape + 取消按钮 */}
      <div
        aria-hidden="true"
        onClick={(): void => onAnswer(false)}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative bg-white/[0.03] border ${TONE_BG[tone]} rounded-xl shadow-2xl max-w-md w-full p-5`}
      >
        <h3 id={titleId} className="text-base font-semibold text-slate-100 mb-2">
          {pending.title}
        </h3>
        {pending.message !== undefined && (
          <div className="text-sm text-slate-400 mb-5">{pending.message}</div>
        )}
        <div className="flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={(): void => onAnswer(false)}
            className="px-4 py-1.5 text-sm rounded border border-white/10 text-slate-300 hover:bg-white/[0.05]"
          >
            {pending.cancelLabel ?? '取消'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={(): void => onAnswer(true)}
            className={`px-4 py-1.5 text-sm rounded ${TONE_BUTTON[tone]}`}
          >
            {pending.confirmLabel ?? '确认'}
          </button>
        </div>
      </div>
    </div>
  );
}
